import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DishSupplementTag from "./DishSupplementTag";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../../components/LanguageVersionSelect/TranslationMissingDot";

interface MenuSupplementGroup {
    id: number;
    name: string;
    nameOrigin?: string | null;
}

interface MenuSupplementsInputProps {
    id?: string;
    selectedIds: number[];
    groups: MenuSupplementGroup[];
    onRemove: (id: number) => void;
    onEdit: (id: number) => void;
    disabled?: boolean;
    canEditSupplement?: boolean;
}

const DishSupplementsInput: React.FC<MenuSupplementsInputProps> = ({
    id,
    selectedIds,
    groups,
    onRemove,
    onEdit,
    disabled = false,
    canEditSupplement = false,
}) => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );

    const supplementsById = useMemo(() => {
        const map = new Map<number, MenuSupplementGroup>();

        groups.forEach((group) => {
            map.set(group.id, group);
        });

        return map;
    }, [groups]);

    const getGroupLabel = (groupId: number): React.ReactNode => {
        const group = supplementsById.get(groupId);
        if (!group) return null;

        const groupNameVm = buildTranslationTextVm({
            translatedValue: group.name,
            originValue: group.nameOrigin,
            isTranslationMode,
        });

        return (
            <span className="d-inline-flex align-items-center">
                <span className="me-1">{groupNameVm.displayText}</span>
                {groupNameVm.isMissingTranslation ? (
                    <TranslationMissingDot tooltipText={t("Edit the language version")} />
                ) : null}
            </span>
        );
    };

    return (
        <>
            <div
                id={id}
                className={`form-control d-flex flex-wrap align-items-center gap-2 py-2 px-3
                 ${disabled ? "bg-secondary opacity-50" : ""}
                `}
                style={{ minHeight: "50px" }}
            >
                {selectedIds.length === 0 && <span className="text-grey fs-7">{t("Select options/additives")}</span>}

                {selectedIds.map((groupId) => (
                    <DishSupplementTag
                        key={groupId}
                        id={groupId}
                        label={getGroupLabel(groupId)}
                        onRemove={onRemove}
                        onEdit={(id) => {
                            if (!canEditSupplement) return;
                            onEdit(id);
                        }}
                        disabled={disabled}
                        canEditSupplement={canEditSupplement}
                    />
                ))}
            </div>
        </>
    );
};

export default DishSupplementsInput;
